import React, { useState, useEffect, useRef } from 'react';
import { Tag as TagIcon, X, Plus, Check, Search, RotateCcw } from 'lucide-react';
import { useTagsStore } from '@/store/tagsStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { CrmTag } from '@/types';
import toast from 'react-hot-toast';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  lojaId?: string | null;
  disabled?: boolean;
  placeholder?: string;
}

const DEFAULT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];
const EMPTY_TAGS: any[] = [];

export function TagSelector({
  selectedTags = [],
  onChange,
  lojaId,
  disabled = false,
  placeholder = 'Selecionar ou criar tag...'
}: TagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [archivedConflict, setArchivedConflict] = useState<{ id: string; name: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { lojaAtivaId } = useLojaAtivaStore();
  const effectiveLojaId = lojaId || lojaAtivaId || null;

  const { fetchTagsForLoja, createTag, reactivateTag, resolveTag } = useTagsStore();
  const rawTags = useTagsStore(state => (effectiveLojaId ? state.tagsByLoja[effectiveLojaId] : undefined));
  const availableTags = rawTags || EMPTY_TAGS;
  const loading = useTagsStore(state => (effectiveLojaId ? Boolean(state.loadingByLoja[effectiveLojaId]) : false));

  // Carrega tags da loja no store compartilhado
  useEffect(() => {
    if (effectiveLojaId) {
      fetchTagsForLoja(effectiveLojaId);
    }
  }, [effectiveLojaId, fetchTagsForLoja]);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setArchivedConflict(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRemoveTag = (tagName: string) => {
    if (disabled) return;
    onChange(selectedTags.filter(t => t.toLowerCase() !== tagName.toLowerCase()));
  };

  const handleToggleTag = (tagSlug: string) => {
    if (disabled) return;
    const exists = selectedTags.some(t => t.toLowerCase() === tagSlug.toLowerCase());
    if (exists) {
      onChange(selectedTags.filter(t => t.toLowerCase() !== tagSlug.toLowerCase()));
    } else {
      onChange([...selectedTags, tagSlug]);
    }
  };

  const handleCreateNewTag = async () => {
    const rawSearch = searchTerm.trim();
    if (!rawSearch || disabled) return;

    if (!effectiveLojaId) {
      // Modo local sem lojaId
      const slug = rawSearch.toLowerCase().replace(/\s+/g, '_');
      if (!selectedTags.some(t => t.toLowerCase() === slug)) {
        onChange([...selectedTags, slug]);
      }
      setSearchTerm('');
      setIsOpen(false);
      return;
    }

    setCreatingTag(true);
    setArchivedConflict(null);
    try {
      const randomColor = DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
      const created = await createTag({
        loja_id: effectiveLojaId,
        nome_exibicao: rawSearch,
        cor: randomColor,
        ia_pode_usar: false
      });

      if (!selectedTags.some(t => t.toLowerCase() === created.nome.toLowerCase())) {
        onChange([...selectedTags, created.nome]);
      }
      setSearchTerm('');
      setIsOpen(false);
      toast.success(`Tag "${created.nome_exibicao || created.nome}" criada com sucesso!`);
    } catch (err: any) {
      if (err.is_archived && err.conflict_id) {
        setArchivedConflict({ id: err.conflict_id, name: rawSearch });
        toast.error(err.message || 'Tag arquivada encontrada.');
      } else {
        toast.error(err.message || 'Erro ao criar tag');
      }
    } finally {
      setCreatingTag(false);
    }
  };

  const handleReactivateConflict = async () => {
    if (!archivedConflict || !effectiveLojaId) return;
    setCreatingTag(true);
    try {
      const reactivated = await reactivateTag(archivedConflict.id, effectiveLojaId);
      if (!selectedTags.some(t => t.toLowerCase() === reactivated.nome.toLowerCase())) {
        onChange([...selectedTags, reactivated.nome]);
      }
      setArchivedConflict(null);
      setSearchTerm('');
      setIsOpen(false);
      toast.success(`Tag "${reactivated.nome_exibicao || reactivated.nome}" reativada com sucesso!`);
    } catch (err: any) {
      toast.error(err.message || 'Falha ao reativar tag.');
    } finally {
      setCreatingTag(false);
    }
  };

  // Filtragem de tags disponíveis (apenas ativas para novas atribuições)
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredTags = availableTags.filter(t => {
    if (t.ativa === false) return false;
    if (!normalizedSearch) return true;
    const matchName = t.nome.toLowerCase().includes(normalizedSearch);
    const matchExibicao = (t.nome_exibicao || '').toLowerCase().includes(normalizedSearch);
    return matchName || matchExibicao;
  });

  const exactMatchExists = availableTags.some(
    t =>
      t.ativa !== false &&
      (t.nome.toLowerCase() === normalizedSearch ||
        (t.nome_exibicao || '').toLowerCase() === normalizedSearch)
  );

  return (
    <div className="relative w-full space-y-1.5" ref={containerRef}>
      {/* Badges de tags selecionadas */}
      <div className="flex flex-wrap gap-2 items-center min-h-[42px] p-2 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl transition-all">
        {selectedTags.length === 0 && !isOpen && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic px-1">Nenhuma tag atribuída</span>
        )}
        {selectedTags.map(tagKey => {
          const tagInfo = resolveTag(effectiveLojaId, tagKey);
          return (
            <span
              key={tagKey}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-white shadow-2xs transition-all"
              style={{ backgroundColor: tagInfo.color }}
            >
              <span>{tagInfo.label}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    handleRemoveTag(tagKey);
                  }}
                  className="hover:bg-black/20 rounded-full p-0.5 transition-colors cursor-pointer"
                  title={`Remover tag "${tagInfo.label}"`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          );
        })}

        {!disabled && (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-lg transition-colors border border-blue-200/80 dark:border-blue-800/80 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Adicionar</span>
          </button>
        )}
      </div>

      {/* Dropdown de Seleção / Criação */}
      {isOpen && !disabled && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-3 space-y-2.5 max-h-72 overflow-y-auto animate-in fade-in zoom-in-95 duration-150 ring-1 ring-black/5">
          {/* Campo de busca */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              autoFocus
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setArchivedConflict(null);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (normalizedSearch && !exactMatchExists) {
                    handleCreateNewTag();
                  }
                }
              }}
              placeholder={placeholder}
              className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-[#111B21] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>

          {/* Banner de conflito com tag arquivada */}
          {archivedConflict && (
            <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg text-xs space-y-2">
              <p className="text-amber-800 dark:text-amber-300 font-medium">
                A tag "{archivedConflict.name}" existe mas está arquivada.
              </p>
              <button
                type="button"
                disabled={creatingTag}
                onClick={handleReactivateConflict}
                className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors cursor-pointer"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reativar e Atribuir</span>
              </button>
            </div>
          )}

          {/* Lista de tags existentes */}
          <div className="space-y-1 max-h-40 overflow-y-auto pt-1">
            {loading ? (
              <div className="text-center py-3 text-xs text-gray-400">Carregando tags...</div>
            ) : filteredTags.length === 0 && !searchTerm.trim() ? (
              <div className="text-center py-3 text-xs text-gray-400">Nenhuma tag cadastrada nesta loja.</div>
            ) : (
              filteredTags.map(tag => {
                const isSelected = selectedTags.some(t => t.toLowerCase() === tag.nome.toLowerCase());
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.nome)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs text-left rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: tag.cor || '#6B7280' }}
                      />
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate">
                        {tag.nome_exibicao || tag.nome}
                      </span>
                      {tag.nome_exibicao && tag.nome_exibicao.toLowerCase() !== tag.nome.toLowerCase() && (
                        <span className="font-mono text-[10px] text-gray-400 truncate">
                          ({tag.nome})
                        </span>
                      )}
                      {tag.descricao && (
                        <span className="text-[10px] text-gray-400 truncate max-w-[140px]">
                          — {tag.descricao}
                        </span>
                      )}
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />}
                  </button>
                );
              })
            )}

            {/* Opção de criar nova tag quando termo não existe exatamente */}
            {normalizedSearch && !exactMatchExists && !archivedConflict && (
              <button
                type="button"
                disabled={creatingTag}
                onClick={handleCreateNewTag}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-colors border border-dashed border-blue-200 dark:border-blue-800/80 cursor-pointer mt-1"
              >
                <Plus className="w-4 h-4" />
                <span>Criar tag "{searchTerm.trim()}"</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

